/**
 * Calendar date utilities — refonte timezone (bug Genève UTC+2, Jul 2026).
 *
 * Le backend stocke et renvoie des datetimes **UTC naïfs** (sans 'Z').
 * `new Date("2026-07-24T11:00:00")` les interprète en heure LOCALE →
 * décalage de -2h à Genève (13h saisi → 11h affiché) et classement
 * "Passées" erroné.
 *
 * Règles end-to-end :
 *   • parse : toute date backend est interprétée comme UTC (`toUtcDayjs`,
 *     `parseUtc`) puis affichée dans la timezone locale du navigateur.
 *   • save  : toute saisie <input type="datetime-local"> est interprétée
 *     dans la timezone du user (dayjs.tz.guess()) puis convertie en UTC ISO
 *     avant l'envoi (`localInputToUtcIso`).
 *
 * Implémenté avec dayjs + plugins utc/timezone.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

dayjs.extend(utc);
dayjs.extend(timezone);

const HAS_TZ_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/;

/** Parse une date backend en objet dayjs (timezone locale). Naïf → UTC. */
export const toUtcDayjs = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  let s = String(value).trim();
  // Précision microsecondes → millisecondes (certains parseurs rejettent .123456)
  s = s.replace(/(\.\d{3})\d+/, '$1');
  if (!HAS_TZ_SUFFIX.test(s)) s += 'Z';
  const d = dayjs(s);
  return d.isValid() ? d : null;
};

/** Parse a backend datetime string as **UTC**. Handles trailing Z, +00:00 and naive.
 *  Retourne un objet Date natif (compatible react-big-calendar & toLocale*). */
export const parseUtc = (value) => {
  const d = toUtcDayjs(value);
  return d ? d.toDate() : null;
};

/** Vrai si la date backend (UTC) est passée par rapport à maintenant. */
export const isPastUtc = (value) => {
  const d = toUtcDayjs(value);
  return d ? d.isBefore(dayjs()) : false;
};

/** Format a Date as the local-time string consumed by <input type="datetime-local"> */
export const toLocalInputValue = (d) => {
  if (!d) return '';
  const v = dayjs(d);
  return v.isValid() ? v.format('YYYY-MM-DDTHH:mm') : '';
};

/** Timezone du navigateur du user (ex: "Europe/Zurich"). */
export const guessUserTz = () => dayjs.tz.guess();

/** Convert the value of an <input type="datetime-local"> back to a UTC ISO string.
 *  La saisie est interprétée dans la timezone du user (ex: Europe/Zurich). */
export const localInputToUtcIso = (value) => {
  if (!value) return null;
  const d = dayjs.tz(value, dayjs.tz.guess());
  return d.isValid() ? d.utc().toISOString() : null;
};
